import {
   createSlice,
   createAsyncThunk,
   createSelector,
   PayloadAction,
} from "@reduxjs/toolkit"
import { handleInvalidVitalValue } from "./utils"
import { RootState } from "."
import { ApiError, ApiResponse } from "@/api"
import {
   InmatesPaginatedResponse,
   requestInmatesData,
   requestCreateInmate,
   requestDeleteInmate,
   requestUpdateInmate,
   requestInmate,
   requestInmateVitals,
   InmateResponse,
   InmateVitalsResponse,
   InmateExportRequest,
   InmateExportResponse,
   requestExportInmateData,
   InmateLogsExportRequest,
   InmateLogsExportResponse,
   requestExportInmateLogs,
} from "@/api/InmatesService"
import { HttpCodes } from "@/constants/globals"
import { Inmate, Sensor } from "@/types/inmate"
import { Log } from "@/types/logs"
import { normalizeMacAddress } from "@/utils/index"

interface InmatesState {
   loading: boolean
   error: number | null
   inmates: Inmate[]
   loadingInmate: boolean
   errorInmate: number | null
   inmateIdToEdit: string | null
   inmateIdToDelete: string | null
   currentInmate: Inmate | null
   loadingCurrentInmate: boolean
   errorCurrentInmate: number | null
   inmateVitals: InmateVitalsResponse | null
   loadingInmateVitals: boolean
   errorInmateVitals: number | null
   inmateLogs: Log[]
   loadingInmateLogs: boolean
   errorInmateLogs: number | null
   loadingInmateExport: boolean
   loadingInmateLogsExport: boolean
}

export const initialState: InmatesState = {
   loading: false,
   error: null,
   inmates: [],
   loadingInmate: false,
   errorInmate: null,
   inmateIdToEdit: null,
   inmateIdToDelete: null,
   currentInmate: null,
   loadingCurrentInmate: false,
   errorCurrentInmate: null,
   inmateVitals: null,
   loadingInmateVitals: false,
   errorInmateVitals: null,
   inmateLogs: [],
   loadingInmateLogs: false,
   errorInmateLogs: null,
   loadingInmateExport: false,
   loadingInmateLogsExport: false,
}

export const fetchInmates = createAsyncThunk(
   "inmates/fetchInmates",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<InmatesPaginatedResponse> =
            await requestInmatesData()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const createInmate = createAsyncThunk(
   "inmates/createInmate",
   async (inmateData: Partial<InmateResponse>, thunkAPI) => {
      try {
         const response: ApiResponse<InmateResponse> =
            await requestCreateInmate(inmateData)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const deleteInmate = createAsyncThunk(
   "inmates/deleteInmate",
   async (inmateId: string, thunkAPI) => {
      thunkAPI.dispatch(setInmateIdToDelete(inmateId))
      try {
         const response: ApiResponse<null> = await requestDeleteInmate(inmateId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const updateInmate = createAsyncThunk(
   "inmates/updateInmate",
   async (
      { id, inmateData }: { id: string; inmateData: Partial<InmateResponse> },
      thunkAPI
   ) => {
      try {
         thunkAPI.dispatch(setInmateIdToEdit(id))
         const response: ApiResponse<InmateResponse> =
            await requestUpdateInmate(id, inmateData)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const fetchInmate = createAsyncThunk(
   "inmates/fetchInmate",
   async (inmateId: string, thunkAPI) => {
      try {
         const response: ApiResponse<InmateResponse> =
            await requestInmate(inmateId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const fetchInmateVitals = createAsyncThunk(
   "inmates/fetchInmateVitals",
   async (inmateId: string, thunkAPI) => {
      try {
         const response: ApiResponse<InmateVitalsResponse> =
            await requestInmateVitals(inmateId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const exportInmateData = createAsyncThunk(
   "inmates/exportInmateData",
   async (exportRequest: InmateExportRequest, thunkAPI) => {
      try {
         const response: ApiResponse<InmateExportResponse> =
            await requestExportInmateData(exportRequest)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)
export const exportInmateLogs = createAsyncThunk(
   "inmates/exportInmateLogs",
   async (exportRequest: InmateLogsExportRequest, thunkAPI) => {
      try {
         const response: ApiResponse<InmateLogsExportResponse> =
            await requestExportInmateLogs(exportRequest)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

const inmatesSlice = createSlice({
   name: "inmates",
   initialState,
   reducers: {
      setInmateIdToEdit(state, action: PayloadAction<string | null>) {
         state.inmateIdToEdit = action.payload
      },
      setInmateIdToDelete(state, action: PayloadAction<string | null>) {
         state.inmateIdToDelete = action.payload
      },
      updateInmateSensor: (
         state,
         action: PayloadAction<{ inmateId: string; sensor: Sensor | null }>
      ) => {
         const inmate = state.inmates.find(
            (inmate) => inmate.id === action.payload.inmateId
         )
         if (inmate) {
            inmate.sensor = action.payload.sensor
         }
      },
      resetInmateVitals(state) {
         state.inmateVitals = null
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchInmates.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchInmates.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<InmatesPaginatedResponse>>
            ) => {
               state.loading = false
               state.inmates = action.payload.data.results
            }
         )
         .addCase(fetchInmates.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(createInmate.pending, (state) => {
            state.loadingInmate = true
            state.errorInmate = null
         })
         .addCase(
            createInmate.fulfilled,
            (state, action: PayloadAction<ApiResponse<InmateResponse>>) => {
               state.loadingInmate = false
               state.inmates.push(action.payload.data)
            }
         )
         .addCase(createInmate.rejected, (state, action) => {
            state.loadingInmate = false
            state.errorInmate = action.payload as number
         })
         .addCase(deleteInmate.pending, (state) => {
            state.loadingInmate = true
            state.errorInmate = null
         })
         .addCase(deleteInmate.fulfilled, (state, action) => {
            state.loadingInmate = false
            state.inmateIdToDelete = null
            state.inmates = state.inmates.filter(
               (inmate) => inmate.id !== action.meta.arg
            )
         })
         .addCase(deleteInmate.rejected, (state, action) => {
            state.loadingInmate = false
            state.errorInmate = action.payload as number
         })
         .addCase(updateInmate.pending, (state) => {
            state.loadingInmate = true
            state.errorInmate = null
         })
         .addCase(updateInmate.fulfilled, (state, action) => {
            state.loadingInmate = false
            state.inmateIdToEdit = null
            const id = action.meta.arg.id
            const { customIdentifier, externalId, firstName, lastName } =
               action.payload.data
            state.inmates = state.inmates.map((inmate) =>
               inmate.id === id
                  ? {
                       ...inmate,
                       customIdentifier,
                       externalId,
                       firstName,
                       lastName,
                    }
                  : inmate
            )
         })
         .addCase(updateInmate.rejected, (state, action) => {
            state.loadingInmate = false
            state.errorInmate = action.payload as number
         })
         .addCase(fetchInmate.pending, (state) => {
            state.loadingCurrentInmate = true
            state.errorCurrentInmate = null
         })
         .addCase(
            fetchInmate.fulfilled,
            (state, action: PayloadAction<ApiResponse<InmateResponse>>) => {
               state.loadingCurrentInmate = false
               state.currentInmate = action.payload.data
            }
         )
         .addCase(fetchInmate.rejected, (state, action) => {
            state.loadingCurrentInmate = false
            state.errorCurrentInmate = action.payload as number
         })
         .addCase(fetchInmateVitals.pending, (state) => {
            state.loadingInmateVitals = true
            state.errorInmateVitals = null
         })
         .addCase(
            fetchInmateVitals.fulfilled,
            (
               state,
               action: PayloadAction<ApiResponse<InmateVitalsResponse>>
            ) => {
               state.loadingInmateVitals = false
               const { heartRate, spo2 } = action.payload.data
               state.inmateVitals = {
                  ...action.payload.data,
                  heartRate: handleInvalidVitalValue(heartRate),
                  spo2: handleInvalidVitalValue(spo2),
               } as unknown as InmateVitalsResponse
            }
         )
         .addCase(fetchInmateVitals.rejected, (state, action) => {
            state.loadingInmateVitals = false
            state.errorInmateVitals = action.payload as number
         })
         .addCase(exportInmateData.pending, (state) => {
            state.loadingInmateExport = true
         })
         .addCase(exportInmateData.fulfilled, (state) => {
            state.loadingInmateExport = false
         })
         .addCase(exportInmateData.rejected, (state) => {
            state.loadingInmateExport = false
         })
         .addCase(exportInmateLogs.pending, (state) => {
            state.loadingInmateLogsExport = true
         })
         .addCase(exportInmateLogs.fulfilled, (state) => {
            state.loadingInmateLogsExport = false
         })
         .addCase(exportInmateLogs.rejected, (state) => {
            state.loadingInmateLogsExport = false
         })
   },
})

export const {
   setInmateIdToEdit,
   setInmateIdToDelete,
   updateInmateSensor,
   resetInmateVitals,
} = inmatesSlice.actions

export default inmatesSlice.reducer

export const selectInmates = (state: RootState) => state.inmatesReducer.inmates

export const selectInmateById = createSelector(
   selectInmates,
   (_state: RootState, inmateId: string | undefined) => inmateId,
   (inmates, inmateId) => inmates.find((inmate) => inmate.id === inmateId)
)

export const selectInmateByWearableId = createSelector(
   selectInmates,
   (inmates) =>
      inmates.reduce<Record<string, Inmate>>((acc, inmate) => {
         if (inmate.sensor?.macAddress) {
            acc[normalizeMacAddress(inmate.sensor.macAddress)] = inmate
         }
         return acc
      }, {})
)
